import { useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import type { Solicitacao } from '../../../models';
import { motivoSolicitacaoLegivel, tipoSolicitacaoExigeMotivo, validarMotivoSolicitacao } from '../../../models';
import { useSolicitacaoMutations } from '../../../hooks/data';
import MotivoSolicitacaoCampos from '../../../components/MotivoSolicitacaoCampos';

interface MotivoSolicitacaoSectionProps {
  solicitacao: Solicitacao;
  /** SI ou quem abriu — mesma regra de "Editar dados/contexto" (firestore.rules). */
  podeEditar: boolean;
}

/** Motivo da Solicitação (Troca de Equipamento, Devolução, Periféricos e Acessórios).
 * Solicitações antigas sem motivo mostram "não informado" e podem ganhar um pela edição. */
export default function MotivoSolicitacaoSection({ solicitacao, podeEditar }: MotivoSolicitacaoSectionProps) {
  const { atualizarSolicitacao } = useSolicitacaoMutations();
  const [editando, setEditando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [detalhe, setDetalhe] = useState('');

  if (!tipoSolicitacaoExigeMotivo(solicitacao.tipoSolicitacao)) return null;

  const pendente = validarMotivoSolicitacao(solicitacao.tipoSolicitacao, motivo, detalhe);

  const handleAbrir = () => {
    setMotivo(solicitacao.motivoSolicitacao ?? '');
    setDetalhe(solicitacao.motivoSolicitacaoDetalhe ?? '');
    setEditando(true);
  };

  const handleSalvar = () => {
    atualizarSolicitacao.mutate(
      {
        id: solicitacao.id,
        motivoSolicitacao: motivo,
        motivoSolicitacaoDetalhe: detalhe.trim() || null,
      },
      { onSuccess: () => setEditando(false) }
    );
  };

  return (
    <div className="rounded-md border border-gray-200 p-4">
      <p className="text-xs font-medium uppercase text-gray-400">Motivo</p>
      {editando ? (
        <div className="mt-2 space-y-2">
          <MotivoSolicitacaoCampos
            tipoSolicitacao={solicitacao.tipoSolicitacao}
            motivo={motivo}
            detalhe={detalhe}
            onChangeMotivo={setMotivo}
            onChangeDetalhe={setDetalhe}
          />
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={handleSalvar}
              disabled={pendente !== null || atualizarSolicitacao.isPending}
            >
              Salvar
            </button>
            <button type="button" className="text-sm text-gray-500 underline" onClick={() => setEditando(false)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-1 flex items-start justify-between gap-2">
          <p className="text-sm text-gray-700">
            {motivoSolicitacaoLegivel(solicitacao.motivoSolicitacao, solicitacao.motivoSolicitacaoDetalhe) || (
              <span className="text-gray-400">Não informado</span>
            )}
          </p>
          {podeEditar && (
            <button
              type="button"
              className="shrink-0 text-gray-400 hover:text-gray-600"
              onClick={handleAbrir}
              title="Editar motivo"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
